import { i18n } from './i18n';

/**
 * Стандартные форматтеры для шаблонов
 */
export const formatters = {
  // Безопасное экранирование HTML (защита от XSS)
  escape: (val: any): string => {
    if (val === null || val === undefined || val === '') return '';
    return String(val)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  },

  // Форматирование даты
  date: (val: any): string => {
    if (!val) return i18n.t('ui.not_specified');
    try {
      return new Date(val).toLocaleDateString(i18n.lang === 'ru' ? 'ru-RU' : 'en-US', {
        year: 'numeric', month: 'long', day: 'numeric'
      });
    } catch {
      return i18n.t('ui.not_specified');
    }
  },

  datetime: (val: any): string => {
    if (!val) return i18n.t('ui.not_specified');
    try {
      return new Date(val).toLocaleString(i18n.lang === 'ru' ? 'ru-RU' : 'en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return i18n.t('ui.not_specified');
    }
  },

  // Превращение переносов строк в <br> (использовать с {{{}}} в шаблоне)
  html: (val: any): string | null => {
    if (!val) return null;
    return formatters.escape(val).replace(/\n/g, '<br>');
  },
  
  pureHtml: (val: any): string | null => {
    if (!val) return null;
    return val.replace(/\n/g, '<br>');
  },

  // Числовое значение с единицей измерения
  withUnit: (val: any, unit: string): string => {
    return val != null ? `${val} ${unit}` : i18n.t('ui.not_specified');
  }
};

/**
 * Правило маппинга поля
 */
export type MappingRule = {
  key: string;                    // Ключ в исходном объекте (например, 'date_from')
  format?: (val: any) => any;     // Функция форматирования (по умолчанию formatters.escape)
  fallback?: any;                 // Значение, если поле null/undefined
};

/**
 * Универсальная функция для подготовки данных шаблона
 * @param source Исходный объект данных (например, Vessel)
 * @param rules Правила маппинга полей
 * @param extra Дополнительные данные, которые нужно добавить "как есть" (например, i18n или photo)
 */
export function buildTemplateData(
  source: Record<string, any>,
  rules: Record<string, MappingRule>,
  extra: Record<string, any> = {}
): Record<string, any> {
  const result: Record<string, any> = { ...extra };

  for (const [targetKey, rule] of Object.entries(rules)) {
    // ИСПРАВЛЕНО: используем getNestedValue вместо source[rule.key]
    let val = getNestedValue(source, rule.key);

    // Обработка null/undefined/пустой строки
    if (val === null || val === undefined || val === '') {
      result[targetKey] = rule.fallback !== undefined ? rule.fallback : null;
    } else {
      // Применяем форматтер или используем безопасное экранирование по умолчанию
      result[targetKey] = rule.format ? rule.format(val) : formatters.escape(val);
    }
  }

  return result;
}

// Убедитесь, что эта функция есть в файле (если нет — добавьте):
function getNestedValue(obj: Record<string, any>, path: string): any {
  return path.split('.').reduce((current, key) => 
    (current && typeof current === 'object' && key in current ? current[key] : undefined), obj);
}